/**
 * Phase 286.b — Super-Admin Notification Audit Dashboard Tile
 *
 * Cross-tenant feed of the `notification_dispatches` audit trail written
 * by the Phase 286 unified dispatcher. Same UX skeleton as the Phase 89E.c
 * Webhook Health tile (overview metrics → channel-status filter → table).
 *
 * Wires to:
 *   GET /api/super-admin/notification-audit/dispatches
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, RefreshCw, MailWarning, Mail, MessageCircle, Crown, CheckCircle2, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BUCKET_BADGE = {
  sent: { label: 'Sent', className: 'bg-emerald-600 hover:bg-emerald-600 text-white' },
  partial: { label: 'Partial', className: 'border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40', variant: 'outline' },
  failed_creds: { label: 'No login!', className: 'border-rose-400 text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40', variant: 'outline' },
};

const fmtTs = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

const channelChip = (status) => {
  if (status === 'sent') return { tone: 'text-emerald-600', label: 'sent' };
  if (status === 'failed') return { tone: 'text-rose-600 font-semibold', label: 'failed' };
  if (status === 'scheduled') return { tone: 'text-indigo-600', label: 'scheduled' };
  if (status === 'skipped') return { tone: 'text-slate-400', label: 'skipped' };
  return { tone: 'text-slate-400', label: status || '—' };
};

const Metric = ({ label, value, tone, testid }) => {
  const palette = {
    slate: 'text-slate-700 dark:text-slate-200',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    amber: 'text-amber-700 dark:text-amber-300',
    rose: 'text-rose-700 dark:text-rose-300',
    indigo: 'text-indigo-700 dark:text-indigo-300',
  }[tone || 'slate'];
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-2xl font-display font-semibold tabular-nums ${palette}`} data-testid={testid}>{value}</span>
    </div>
  );
};

export default function NotificationAuditTile() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refiringId, setRefiringId] = useState(null);
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [personaFilter, setPersonaFilter] = useState('all');
  const [windowDays, setWindowDays] = useState('7');

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (personaFilter && personaFilter !== 'all') params.set('persona', personaFilter);
      params.set('since_days', windowDays);
      const r = await api.get(`/super-admin/notification-audit/dispatches?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load notification audit');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, personaFilter, windowDays]);

  useEffect(() => { load(false); }, [load]);

  const refireDispatch = useCallback(async (row) => {
    const ok = window.confirm(
      `Re-fire credentials for ${row.email}?\n\n` +
      `This will:\n` +
      `• rotate their temporary password\n` +
      `• re-send credentials email + WhatsApp welcome\n` +
      `• schedule a fresh CEO welcome in 5 min\n\n` +
      `Continue?`
    );
    if (!ok) return;
    setRefiringId(row.id);
    try {
      const r = await api.post(`/super-admin/notification-audit/dispatches/${row.id}/refire`);
      const creds = r.data?.credentials?.status;
      if (creds === 'sent') {
        toast.success(`Credentials re-sent to ${row.email}`);
      } else {
        toast.warning(`Re-fire completed but credentials channel: ${creds}`);
      }
      await load(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Re-fire failed');
    } finally {
      setRefiringId(null);
    }
  }, [load]);

  const overview = data?.overview || { total: 0, sent: 0, partial: 0, failed_creds: 0, failed_any: 0 };
  const channels = data?.overview?.channels || {};
  const rows = data?.rows || [];

  const headlineTone = useMemo(() => {
    if (overview.failed_creds > 0) return 'rose';
    if (overview.failed_any > 0) return 'amber';
    return 'emerald';
  }, [overview]);

  if (loading) {
    return (
      <Card data-testid="notification-audit-tile-loading">
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent><Skeleton className="h-40 w-full" /></CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="notification-audit-tile">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-indigo-100 dark:bg-indigo-950">
              <Bell className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <CardTitle className="text-base">Notification Audit</CardTitle>
              <p className="text-xs text-muted-foreground">Account-creation dispatches across all tenants</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={windowDays} onValueChange={setWindowDays}>
              <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="notification-audit-window-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24h</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="notification-audit-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All dispatches</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="failed_creds">No login (creds failed)</SelectItem>
                <SelectItem value="failed_any">Any failure</SelectItem>
              </SelectContent>
            </Select>
            <Select value={personaFilter} onValueChange={setPersonaFilter}>
              <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="notification-audit-persona-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All personas</SelectItem>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="coach">Coach</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(true)}
              disabled={refreshing}
              data-testid="notification-audit-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overview metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-3 rounded-lg bg-muted/30">
          <Metric label={`Total (${data?.window_days || 7}d)`} value={overview.total} tone="slate" testid="notification-audit-metric-total" />
          <Metric label="Sent" value={overview.sent} tone="emerald" testid="notification-audit-metric-sent" />
          <Metric label="Partial" value={overview.partial} tone="amber" testid="notification-audit-metric-partial" />
          <Metric label="No login!" value={overview.failed_creds} tone={overview.failed_creds > 0 ? 'rose' : 'slate'} testid="notification-audit-metric-failed-creds" />
          <Metric label="Any failure" value={overview.failed_any} tone={overview.failed_any > 0 ? headlineTone : 'slate'} testid="notification-audit-metric-failed-any" />
        </div>

        {/* Per-channel throughput strip */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground border border-dashed rounded-lg p-2.5">
          <span className="font-medium text-slate-700 dark:text-slate-200">Channel breakdown:</span>
          <span className="inline-flex items-center gap-1">
            <Mail className="h-3 w-3 text-emerald-600" />
            <span className="tabular-nums" data-testid="notification-audit-channel-creds-sent">{channels.credentials_sent || 0}</span>
            <span className="text-emerald-600">sent</span>
            {channels.credentials_failed > 0 && (
              <>
                <span className="opacity-50">·</span>
                <span className="tabular-nums text-rose-600 font-semibold" data-testid="notification-audit-channel-creds-failed">
                  {channels.credentials_failed}
                </span>
                <span className="text-rose-600">failed</span>
              </>
            )}
          </span>
          <span className="opacity-50">|</span>
          <span className="inline-flex items-center gap-1">
            <MessageCircle className="h-3 w-3 text-emerald-600" />
            <span className="tabular-nums">{channels.whatsapp_sent || 0}</span>
            <span className="text-emerald-600">WhatsApp</span>
            {channels.whatsapp_skipped > 0 && (
              <>
                <span className="opacity-50">·</span>
                <span className="tabular-nums opacity-60">{channels.whatsapp_skipped}</span>
                <span className="opacity-60">no phone</span>
              </>
            )}
            {channels.whatsapp_failed > 0 && (
              <>
                <span className="opacity-50">·</span>
                <span className="tabular-nums text-rose-600 font-semibold">{channels.whatsapp_failed}</span>
                <span className="text-rose-600">failed</span>
              </>
            )}
          </span>
          <span className="opacity-50">|</span>
          <span className="inline-flex items-center gap-1">
            <Crown className="h-3 w-3 text-indigo-600" />
            <span className="tabular-nums">{channels.ceo_sent || 0}</span>
            <span className="text-emerald-600">CEO sent</span>
            <span className="opacity-50">·</span>
            <span className="tabular-nums text-indigo-600">{channels.ceo_scheduled || 0}</span>
            <span className="text-indigo-600">scheduled</span>
          </span>
        </div>

        {/* Dispatch table */}
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="notification-audit-table">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Tenant</th>
                  <th className="px-3 py-2 font-medium">Persona</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Channels</th>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-10 text-center text-muted-foreground" data-testid="notification-audit-empty">
                      <CheckCircle2 className="h-5 w-5 mx-auto mb-2 text-emerald-500" />
                      No dispatches match this filter.
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const cfg = BUCKET_BADGE[row.bucket] || BUCKET_BADGE.partial;
                  const credsChip = channelChip(row.credentials?.status);
                  const waChip = channelChip(row.whatsapp?.status);
                  const ceoChip = channelChip(row.ceo_welcome?.status);
                  return (
                    <tr
                      key={row.id}
                      className="border-t hover:bg-muted/20 align-top"
                      data-testid={`notification-audit-row-${row.id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800 dark:text-slate-100 truncate max-w-[220px]">{row.email}</div>
                        {row.bucket === 'failed_creds' && (
                          <div className="text-[10px] text-rose-600 mt-0.5 leading-tight">
                            {(row.credentials?.error || 'credentials email failed').slice(0, 80)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-700 dark:text-slate-200">{row.academy_name}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="secondary" className="text-[10px] font-mono">{row.persona || '—'}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge
                          variant={cfg.variant || 'default'}
                          className={`text-[10px] ${cfg.className}`}
                          data-testid={`notification-audit-bucket-${row.id}`}
                        >
                          {cfg.label}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-0.5 text-[10px]">
                          <div className="inline-flex items-center gap-1">
                            <Mail className="h-2.5 w-2.5 opacity-60" />
                            <span className={credsChip.tone}>creds: {credsChip.label}</span>
                          </div>
                          <div className="inline-flex items-center gap-1">
                            <MessageCircle className="h-2.5 w-2.5 opacity-60" />
                            <span className={waChip.tone}>wa: {waChip.label}</span>
                          </div>
                          <div className="inline-flex items-center gap-1">
                            <Crown className="h-2.5 w-2.5 opacity-60" />
                            <span className={ceoChip.tone}>ceo: {ceoChip.label}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate-700 dark:text-slate-200">{fmtTs(row.started_at)}</div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {(row.bucket === 'failed_creds' || row.bucket === 'partial') ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[10px] border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/30"
                            onClick={() => refireDispatch(row)}
                            disabled={refiringId === row.id}
                            data-testid={`notification-audit-refire-${row.id}`}
                          >
                            {refiringId === row.id ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3 w-3 mr-1" />
                            )}
                            Re-fire
                          </Button>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Trust-amplifier: a single line that quantifies the "no login" pain */}
        {overview.failed_creds > 0 && (
          <div
            className="flex items-start gap-2 p-2 rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900"
            data-testid="notification-audit-failed-creds-banner"
          >
            <MailWarning className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-rose-700 dark:text-rose-300 leading-relaxed">
              <span className="font-bold">{overview.failed_creds} user{overview.failed_creds === 1 ? '' : 's'}</span>{' '}
              never received their login credentials in the last {data?.window_days || 7} days. These accounts exist
              in the DB but the recipient cannot sign in — investigate Resend deliverability or trigger a
              credential-reset via the academy settings page.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
